import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import logger from '../../lib/logger';
import {
  Activity, GitBranch, MessageSquare, Send, Eye, Check, X,
  Loader2, RefreshCw, Tag, Gauge, UserCircle2, Headset,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  version: { icon: GitBranch, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Version' },
  comment: { icon: MessageSquare, color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Comment' },
  sent: { icon: Send, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Sent' },
  opened: { icon: Eye, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Opened' },
  approval: { icon: Check, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', label: 'Approval' },
  // v260.57 — manual workflow-status overrides surfaced in the per-estimate
  // Activity tab so all "what's been done to this deal" lives in one place.
  status: { icon: Tag, color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Status' },
  // v260.122 — Maturity-driven readiness slider override. Distinct icon/colour
  // so finance can scan for pricing decisions in a long activity timeline.
  maturity_override: { icon: Gauge, color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'Maturity Override' },
  // Sales Lead ownership reassignment — auditable owner changes.
  sales_lead: { icon: UserCircle2, color: 'text-[#0F2040]', bg: 'bg-[#4ABFCF]/10', border: 'border-[#4ABFCF]/30', label: 'Sales Lead' },
  // CS Lead assignment — auditable Customer Success owner changes.
  cs_lead: { icon: Headset, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'CS Lead' },
};

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function WorkspaceActivityTab() {
  const { estimateId } = useOutletContext();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchActivity = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/activity-log`, { withCredentials: true });
      setActivities(res.data.activities || []);
    } catch (err) {
      logger.error('Failed to fetch activity:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [estimateId]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  if (loading) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="workspace-activity">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-[#0F2040]">Activity Log</h2>
          <Badge variant="secondary" className="text-[9px]">{activities.length}</Badge>
        </div>
        <button
          onClick={() => fetchActivity(true)}
          disabled={refreshing}
          className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#4ABFCF] transition-colors"
          data-testid="activity-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {activities.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Activity className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No activity yet for this estimate.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />

          <div className="space-y-0">
            {activities.map((act, idx) => {
              const config = TYPE_CONFIG[act.type] || TYPE_CONFIG.version;
              const IconComp = config.icon;
              return (
                <div key={`${act.type}-${act.timestamp}-${idx}`} className="relative flex items-start gap-3 py-3">
                  {/* Icon dot */}
                  <div className={`relative z-10 w-10 h-10 rounded-full ${config.bg} border ${config.border} flex items-center justify-center shrink-0`}>
                    <IconComp className={`w-4 h-4 ${config.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-[9px] py-0 ${config.color} border-current/30`}>
                        {config.label}
                      </Badge>
                      {act.version_number && (
                        <Badge variant="secondary" className="text-[9px] py-0">v{act.version_number}</Badge>
                      )}
                      {act.status === 'approved' && (
                        <Badge className="bg-green-100 text-green-700 text-[9px] py-0">Approved</Badge>
                      )}
                      {act.status === 'changes_requested' && (
                        <Badge className="bg-amber-100 text-amber-700 text-[9px] py-0">Changes Requested</Badge>
                      )}
                    </div>
                    <p className="text-xs text-[#0F2040] mt-1">{act.summary}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400">{act.user}</span>
                      <span className="text-[10px] text-slate-300">·</span>
                      <span className="text-[10px] text-slate-400">{formatTimestamp(act.timestamp)}</span>
                    </div>
                    {act.comment && (
                      <p className="text-[10px] text-slate-500 mt-1 italic border-l-2 border-slate-200 pl-2">
                        "{act.comment}"
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
